import { RootState } from '@/store';
import { type ClassValue, clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { badgeConfig } from './constent';
import moment from 'moment';
import { useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { UserRole } from '@/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function parseJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export function formatNumberWithCommas(
  number: number | string | undefined,
  decimals: number = 2
): string {
  const safeNumber = Number(number) || 0;

  return safeNumber.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

export const shortenHash = (hash: string, length: number = 6) => {
  if (!hash) return '—';
  return `${hash.slice(0, length)}...${hash.slice(-4)}`;
};

type ToastType = 'success' | 'error' | 'info' | 'warning';

export const showToastMessage = (type: ToastType, message: string) => {
  toast.dismiss();
  switch (type) {
    case 'success':
      toast.success(message);
      break;
    case 'error':
      toast.error(message);
      break;
    case 'info':
      toast(message);
      break;
    case 'warning':
      toast(message, {
        icon: '⚠️',
      });
      break;
    default:
      toast(message);
  }
};

export const useRoleIds = () => {
  const roles = useSelector((state: RootState) => state.roles.availableRoles);
  const getRoleIdByName = (name: string) => {
    if (!roles || roles.length === 0) return null;
    const role = roles.find((r) => r.name.toLowerCase() === name.toLowerCase());
    return role?.id ?? null;
  };
  return {
    superAdminID: getRoleIdByName('SUPER ADMIN'),
    adminID: getRoleIdByName('ADMIN'),
    // whitelabelId: getRoleIdByName('WHITELABEL'),
    // subAdminId: getRoleIdByName('SUB ADMIN'),
    superMasterId: getRoleIdByName('SUPER MASTER'),
    masterId: getRoleIdByName('MASTER'),
    userId: getRoleIdByName('USER'),
    bankerId: getRoleIdByName('BANKER'),
  };
};
export function getRoleBadge(roleName: string) {
  const key = roleName?.toLowerCase();

  const badge = badgeConfig[key];

  if (!badge) {
    return { code: 'NA', bg: 'bg-gray' };
  }

  return badge;
}

// utils/clipboard.ts
export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    showToastMessage('success', `Copied to clipboard: ${text}`);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

export const getErrorMessage = (err: any) => {
  if (!err) return 'Something went wrong';

  if (err?.data?.message) return err.data.message;

  if (err?.error) return err.error;

  return 'Something went wrong';
};

export const formatTimestamp = (timestamp: string | null) => {
  return moment(timestamp).format('DD MMM YYYY h:mm A');
};
export const setLocalData = <T>(key: string, value: T): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const getLocalData = <T>(key: string): T | null => {
  const stored = localStorage.getItem(key);
  return stored ? (JSON.parse(stored) as T) : null;
};
type AmountSign = '+' | '-';

export const getAmountColorBySign = (sign?: AmountSign) => {
  if (sign === '+') return 'text-green2';
  if (sign === '-') return 'text-red-text';
  return 'text-gray-600';
};
type RefreshFunction = () => Promise<any>;

export const useRefresh = (refreshFn: RefreshFunction, delay: number = 500) => {
  const [rotating, setRotating] = useState(false);

  const handleRefresh = () => {
    setRotating(true);
    setTimeout(() => {
      refreshFn().finally(() => setRotating(false));
    }, delay);
  };

  return { rotating, handleRefresh };
};
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export const getProfitLossClass = (profitLoss: number) =>
  profitLoss > 0
    ? 'text-green2 font-semibold'
    : profitLoss < 0
      ? 'text-red-500 font-semibold'
      : 'text-black font-semibold';

export const valueFormatter = Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});
export const formatContextLabel = (value?: string) => {
  if (!value) return '--';

  if (!/[A-Z]/.test(value.slice(1))) {
    return value;
  }

  return value.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
};
const roleNameToNextNameMap: Partial<Record<UserRole, string[]>> = {
  OWNER: ['USER', 'SUPER ADMIN', 'ADMIN', 'MASTER', 'SUPER MASTER'],
  'SUPER ADMIN': ['USER', 'ADMIN', 'MASTER', 'SUPER MASTER'],
  ADMIN: ['USER', 'SUPER MASTER', 'MASTER'],
  'SUPER MASTER': ['USER', 'MASTER'],
  MASTER: ['USER'],
};
export const getAllowedRoles = (role?: string): string[] => {
  if (!role) return [];

  if (role in roleNameToNextNameMap) {
    return roleNameToNextNameMap[role as UserRole] ?? [];
  }

  return [];
};
